<template>
  <div class="operations-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER & BREADCRUMBS ==================== -->
    <div class="op-header-row mb-6">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="op-badge-section">
            <v-icon x-small color="#082C69" class="me-1">mdi-routes</v-icon>
            المسارات
          </span>
          <div class="op-breadcrumb d-flex align-center text-muted caption">
            <router-link to="/operations/routes" class="op-bc-link text-decoration-none">المسارات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="font-weight-bold text-navy">{{ group.group_code }} — {{ group.name }}</span>
          </div>
        </div>

        <div class="d-flex align-center flex-wrap gap-2">
          <span class="op-group-code-pill font-monospace font-weight-bold">{{ group.group_code }}</span>
          <h1 class="op-main-title mb-0">{{ group.name }}</h1>
          <span :class="['op-status-badge', getGroupStatusClass(group.status)]">
            {{ group.status_label }}
          </span>
        </div>
        <p class="op-subtitle mt-1">عرض جميع الرحلات الأساسية التابعة لهذه المجموعة ومساراتها وتوقيتاتها</p>
      </div>

      <!-- Header Actions -->
      <div class="op-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          to="/operations/routes"
          class="op-btn-back"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          <span>العودة للمسارات</span>
        </v-btn>

        <v-btn
          depressed
          class="op-btn-refresh"
          :loading="isLoading"
          :disabled="isLoading"
          @click="fetchGroupData"
        >
          <v-icon size="18" color="#082C69">mdi-refresh</v-icon>
          <span class="ms-1 font-weight-bold">تحديث</span>
        </v-btn>
      </div>
    </div>

    <!-- ==================== 4 SUMMARY CARDS ==================== -->
    <div class="op-kpi-grid mb-6">
      <!-- 1. Trips Count -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">إجمالي الرحلات الأساسية</div>
          <div class="kpi-value text-navy">{{ summary.trips_count }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #EEF2FF; color: #082C69;">
          <v-icon size="22" color="#082C69">mdi-bus-clock</v-icon>
        </div>
      </div>

      <!-- 2. Routes Count -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">عدد المسارات</div>
          <div class="kpi-value text-blue">{{ summary.routes_count }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #E0F2FE; color: #0284C7;">
          <v-icon size="22" color="#0284C7">mdi-routes</v-icon>
        </div>
      </div>

      <!-- 3. Total Distance -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">إجمالي المسافة</div>
          <div class="kpi-value text-emerald">{{ summary.total_distance_km }} <small class="text-sm">كم</small></div>
        </div>
        <div class="kpi-icon-side" style="background-color: #DCFCE7; color: #16A34A;">
          <v-icon size="22" color="#16A34A">mdi-road</v-icon>
        </div>
      </div>

      <!-- 4. Published / Created At -->
      <div class="op-kpi-card">
        <div class="kpi-content-side">
          <div class="kpi-label">{{ group.published_at ? 'تاريخ النشر' : 'تاريخ الإنشاء' }}</div>
          <div class="kpi-value-date">{{ group.published_at || group.created_at || '—' }}</div>
        </div>
        <div class="kpi-icon-side" style="background-color: #FEF3C7; color: #D97706;">
          <v-icon size="22" color="#D97706">mdi-calendar-check</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== FILTER BAR ==================== -->
    <div class="op-filter-bar mb-6">
      <div class="filter-inputs-group">
        <!-- Search Input -->
        <div class="filter-search-box">
          <v-text-field
            v-model="filters.search"
            prepend-inner-icon="mdi-magnify"
            placeholder="البحث بكود الرحلة، كود أو اسم المسار..."
            outlined
            dense
            clearable
            hide-details
            class="op-search-input"
            @input="onSearchInput"
            @keydown.enter="onSearchEnter"
            @click:clear="onSearchClear"
          ></v-text-field>
        </div>

        <!-- Route Filter -->
        <div class="filter-select-box">
          <v-select
            v-model="filters.route_id"
            :items="routeFilterOptions"
            label="المسار"
            outlined
            dense
            hide-details
            class="op-filter-select"
            @change="fetchGroupData"
          ></v-select>
        </div>
      </div>

      <div class="filter-actions-group">
        <button type="button" class="op-filter-btn" @click="resetFilters">
          <v-icon size="16" color="#63738A" class="me-1">mdi-restore</v-icon>
          <span>إعادة تعيين</span>
        </button>
      </div>
    </div>

    <!-- ==================== FULL TRIPS TABLE CARD ==================== -->
    <div class="op-card mb-6">
      <div class="op-card-header d-flex align-center justify-space-between py-4 px-6 border-bottom">
        <div class="d-flex align-center">
          <div class="op-icon-header-box me-3">
            <v-icon size="20" color="#082C69">mdi-bus-multiple</v-icon>
          </div>
          <div>
            <h2 class="op-card-title mb-0">قائمة الرحلات الأساسية</h2>
            <p class="op-card-subtitle mb-0">عرض شامل لجميع الرحلات ومواعيد انطلاقها ومسافاتها</p>
          </div>
        </div>

        <span class="op-count-badge font-weight-bold">
          {{ trips.length }} رحلات أساسية
        </span>
      </div>

      <div v-if="trips.length > 0" class="table-responsive">
        <table class="op-trips-table">
          <thead>
            <tr>
              <th style="width: 40px;" class="text-center">#</th>
              <th>كود الرحلة</th>
              <th>المسار</th>
              <th>كود المسار</th>
              <th>أيام التشغيل</th>
              <th>وقت الانطلاق</th>
              <th>وقت الوصول المتوقع</th>
              <th>المدة المتوقعة</th>
              <th class="text-center">الحالة</th>
              <th class="text-center" style="width: 80px;">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="trip in trips" :key="trip.id">
              <td class="text-center text-muted font-weight-medium">{{ trip.index }}</td>
              <td>
                <span class="op-trip-code-pill font-monospace font-weight-bold">{{ trip.trip_code }}</span>
              </td>
              <td>
                <span class="font-weight-bold text-navy">{{ trip.route_name }}</span>
              </td>
              <td>
                <span class="op-route-code-pill font-monospace">{{ trip.route_code }}</span>
              </td>
              <td>
                <span class="op-days-badge">{{ trip.formatted_operating_days }}</span>
              </td>
              <td class="caption font-weight-bold text-navy">{{ trip.first_stop_time_formatted }}</td>
              <td class="caption font-weight-bold text-navy">{{ trip.last_stop_time_formatted }}</td>
              <td class="caption text-muted font-weight-medium">{{ trip.expected_duration }}</td>
              <td class="text-center">
                <span :class="['op-trip-status-pill', trip.status_id === 1 ? 'status-active' : 'status-inactive']">
                  {{ trip.status_label }}
                </span>
              </td>
              <td class="text-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      class="op-action-btn"
                      @click="openTripDetails(trip)"
                    >
                      <v-icon small color="#082C69">mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>عرض التفاصيل</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-10 text-muted">
        <v-icon size="32" color="#94A3B8" class="d-block mb-2">mdi-bus-off</v-icon>
        <span>لا توجد رحلات مطابقة لمعايير البحث الحالية في هذه المجموعة</span>
      </div>
    </div>

    <!-- ==================== VIEW-ONLY TRIP DETAILS MODAL ==================== -->
    <v-dialog v-model="tripDetailsDialog" max-width="850px" scrollable>
      <v-card v-if="selectedTripDetails" class="op-dialog-card" dir="rtl">
        <v-card-title class="op-dialog-header d-flex align-center justify-space-between py-4 px-6">
          <div class="d-flex align-center gap-2 flex-wrap">
            <div class="op-icon-header-box me-2">
              <v-icon size="20" color="#082C69">mdi-bus</v-icon>
            </div>
            <div>
              <div class="d-flex align-center gap-2">
                <span class="op-trip-code-pill font-monospace font-weight-bold">{{ selectedTripDetails.trip.trip_code }}</span>
                <span class="op-route-code-pill font-monospace">{{ selectedTripDetails.route.route_code }}</span>
                <span :class="['op-trip-status-pill', selectedTripDetails.trip.status_id === 1 ? 'status-active' : 'status-inactive']">
                  {{ selectedTripDetails.trip.status_label }}
                </span>
              </div>
              <h3 class="op-dialog-title mb-0 mt-1">{{ selectedTripDetails.route.name }}</h3>
            </div>
          </div>
          <v-btn icon small @click="tripDetailsDialog = false">
            <v-icon color="#64748B">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- 4 Stat Cards Grid -->
          <div class="op-trip-modal-grid mb-5">
            <div class="op-modal-stat-card">
              <span class="stat-label">وقت الانطلاق</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.first_stop_time_formatted }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">وقت الوصول المتوقع</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.last_stop_time_formatted }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">المدة المتوقعة</span>
              <span class="stat-value text-navy">{{ selectedTripDetails.trip.expected_duration }}</span>
            </div>
            <div class="op-modal-stat-card">
              <span class="stat-label">أيام التشغيل</span>
              <span class="stat-value text-amber font-weight-bold">{{ selectedTripDetails.trip.formatted_operating_days }}</span>
            </div>
          </div>

          <!-- Stops Timetable -->
          <h4 class="font-weight-bold text-navy mb-3 d-flex align-center">
            <v-icon small color="#082C69" class="me-1">mdi-map-marker-path</v-icon>
            محطات المسار ومواعيد الوصول المقدرة ({{ selectedTripDetails.stops.length }} محطة)
          </h4>

          <div class="table-responsive border rounded-lg mb-5">
            <table class="op-trips-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center">#</th>
                  <th>اسم المحطة</th>
                  <th>العنوان</th>
                  <th>وقت الوصول (ETA)</th>
                  <th class="text-center">زمن الطريق (دقيقة)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stop in selectedTripDetails.stops" :key="stop.order">
                  <td class="text-center text-muted font-weight-bold">{{ stop.order }}</td>
                  <td class="font-weight-bold text-navy">{{ stop.name }}</td>
                  <td class="caption text-muted">{{ stop.address || '—' }}</td>
                  <td class="caption font-weight-bold text-emerald">{{ stop.eta_formatted || '—' }}</td>
                  <td class="text-center caption">{{ stop.road_minutes_from_previous || '0' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Google Map View -->
          <div v-if="selectedTripDetails.map && selectedTripDetails.map.markers.length > 0" class="mt-4">
            <h4 class="font-weight-bold text-navy mb-2 d-flex align-center">
              <v-icon small color="#082C69" class="me-1">mdi-map-outline</v-icon>
              خريطة المسار والمحطات
            </h4>
            <div class="op-modal-map-container">
              <GoogleMapLoader
                :apiKey="googleMapsApiKey"
                :mapConfig="mapConfig"
                :center="mapCenter"
                :zoom="12"
                :markers="mapMarkers"
                :polylines="mapPolylines"
                class="op-map-element"
              />
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4 bg-slate-50 justify-end">
          <v-btn depressed class="op-btn-secondary" @click="tripDetailsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { Keys } from "@/config.js";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";

export default {
  name: "OperationsGroupRoutes",
  components: {
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      groupId: null,
      group: {},
      summary: {
        trips_count: 0,
        routes_count: 0,
        total_distance_km: 0,
      },
      routes: [],
      trips: [],
      filters: {
        search: "",
        route_id: "all",
      },
      searchDebounceTimer: null,
      // Trip Details Modal
      tripDetailsDialog: false,
      selectedTripDetails: null,
      googleMapsApiKey: Keys.GOOGLE_MAPS_API_KEY || "",
      mapConfig: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
      },
    };
  },
  computed: {
    routeFilterOptions() {
      const opts = [{ value: "all", text: "كل المسارات" }];
      if (Array.isArray(this.routes)) {
        this.routes.forEach((r) => {
          opts.push({ value: r.id, text: r.code ? `${r.code} — ${r.name}` : r.name });
        });
      }
      return opts;
    },
    mapCenter() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.markers &&
        this.selectedTripDetails.map.markers.length > 0
      ) {
        return this.selectedTripDetails.map.markers[0].position;
      }
      return { lat: 30.0444, lng: 31.2357 };
    },
    mapMarkers() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.markers
      ) {
        return this.selectedTripDetails.map.markers;
      }
      return [];
    },
    mapPolylines() {
      if (
        this.selectedTripDetails &&
        this.selectedTripDetails.map &&
        this.selectedTripDetails.map.polyline &&
        this.selectedTripDetails.map.polyline.length > 0
      ) {
        return [
          {
            data: this.selectedTripDetails.map.polyline,
            strokeColor: "#082C69",
            strokeWeight: 5,
            strokeOpacity: 0.9,
          },
        ];
      }
      return [];
    },
  },
  created() {
    this.groupId = this.$route.params.groupId;
    if (this.groupId) {
      this.fetchGroupData();
    } else {
      this.$router.push("/operations/routes");
    }
  },
  methods: {
    fetchGroupData() {
      this.isLoading = true;
      const params = {};
      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.route_id && this.filters.route_id !== "all") params.route_id = this.filters.route_id;

      axios
        .get(`/operations/routes/groups/${this.groupId}`, { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const data = res.data.data;
            this.group = data.group || {};
            this.summary = data.summary || {};
            this.routes = data.routes || [];
            this.trips = data.trips || [];
          }
        })
        .catch((err) => {
          console.error("Error loading group details:", err);
          const status = err.response ? err.response.status : null;
          if (status === 403) {
            this.notifyUser("error", "غير مصرح", "غير مصرح لك بالوصول إلى هذه المجموعة.");
            this.$router.push("/operations/routes");
          } else {
            this.notifyUser("error", "خطأ", "تعذر تحميل تفاصيل المجموعة.");
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchGroupData();
      }, 350);
    },
    onSearchEnter() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.fetchGroupData();
    },
    onSearchClear() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.filters.search = "";
      this.$nextTick(() => {
        this.fetchGroupData();
      });
    },
    resetFilters() {
      this.filters = {
        search: "",
        route_id: "all",
      };
      this.fetchGroupData();
    },
    openTripDetails(trip) {
      this.isLoading = true;
      axios
        .get(`/operations/routes/trips/${trip.id}`)
        .then((res) => {
          if (res.data && res.data.success) {
            this.selectedTripDetails = res.data.data;
            this.tripDetailsDialog = true;
          }
        })
        .catch((err) => {
          console.error("Error loading trip details:", err);
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "تعذر تحميل تفاصيل الرحلة.";
          this.notifyUser("error", "خطأ", msg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getGroupStatusClass(status) {
      if (status === "published") return "badge-published";
      if (status === "draft") return "badge-draft";
      return "badge-inactive";
    },
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : "خطأ"),
          text: text,
          type: type === "warn" ? "warn" : type,
        });
      } else if (this.$swal) {
        this.$swal(title || (type === "success" ? "تم بنجاح" : "خطأ"), text, type === "warn" ? "warning" : type);
      } else {
        alert(text);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.operations-container {
  font-family: inherit;
  color: #1E293B;
}

.op-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.op-badge-section {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background-color: #EEF2F6;
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
}

.op-bc-link {
  color: #082C69 !important;
  font-weight: 600;

  &:hover {
    text-decoration: underline !important;
  }
}

.op-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  letter-spacing: -0.5px;
  margin: 0;
}

.op-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 4px 0 0 0;
}

.op-btn-back {
  height: 40px !important;
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 10px !important;
  color: #475569 !important;
  font-weight: 600 !important;
}

.op-btn-refresh {
  height: 40px !important;
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 10px !important;
  color: #082C69 !important;
  padding: 0 16px !important;
}

/* KPI Cards Grid */
.op-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.op-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.kpi-content-side {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.2;
}

.kpi-value-date {
  font-size: 14px;
  font-weight: 700;
  color: #082C69;
  line-height: 1.2;
}

.kpi-icon-side {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter Bar */
.op-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  gap: 12px;
  flex-wrap: wrap;
}

.filter-inputs-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
  flex-wrap: wrap;
}

.filter-search-box {
  flex-grow: 2;
  min-width: 260px;
}

.filter-select-box {
  flex-grow: 1;
  min-width: 180px;
}

.filter-actions-group {
  display: flex;
  align-items: center;
}

.op-filter-btn {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }
}

/* Main Table Card */
.op-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.op-card-header {
  background: #FAFCFE;
}

.op-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
}

.op-card-subtitle {
  font-size: 12px;
  color: #64748B;
  margin-top: 2px;
}

.op-count-badge {
  padding: 4px 12px;
  background: #EEF2F6;
  color: #082C69;
  border-radius: 20px;
  font-size: 12px;
}

/* Table */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.op-trips-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.op-trips-table th {
  padding: 11px 14px;
  background-color: #F8FAFC;
  color: #475569;
  font-weight: 700;
  text-align: right;
  border-bottom: 1px solid #E2E8F0;
  white-space: nowrap;
}

.op-trips-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.op-trips-table tr:hover td {
  background-color: #F8FAFC;
}

.op-trip-code-pill {
  display: inline-block;
  padding: 3px 8px;
  background: #EEF2F6;
  border-radius: 6px;
  color: #082C69;
  font-size: 12px;
}

.op-group-code-pill {
  padding: 4px 10px;
  background: #EEF2F6;
  border-radius: 8px;
  color: #082C69;
  font-size: 13px;
  font-weight: 800;
}

.op-route-code-pill {
  display: inline-block;
  padding: 2px 7px;
  background: #F1F5F9;
  border-radius: 6px;
  color: #64748B;
  font-size: 11px;
}

.op-days-badge {
  display: inline-block;
  padding: 3px 10px;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 12px;
  color: #B45309;
  font-size: 11px;
  font-weight: 600;
}

.op-trip-status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
}

.status-active {
  background: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}

.status-inactive {
  background: #FEF2F2;
  color: #991B1B;
  border: 1px solid #FECACA;
}

.op-status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.badge-published {
  background-color: #ECFDF5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}

.badge-draft {
  background-color: #FEF3C7;
  color: #92400E;
  border: 1px solid #FDE68A;
}

.badge-inactive {
  background-color: #F1F5F9;
  color: #475569;
  border: 1px solid #CBD5E1;
}

.op-action-btn {
  background: #F1F5F9 !important;
  border-radius: 8px !important;

  &:hover {
    background: #E2E8F0 !important;
  }
}

/* Modal */
.op-dialog-card {
  border-radius: 14px;
}

.op-dialog-header {
  background: #FAFCFE;
}

.op-icon-header-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #EEF2F6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
}

.op-trip-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
}

.op-modal-stat-card {
  padding: 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}

.op-modal-stat-card .stat-label {
  font-size: 11px;
  color: #64748B;
  margin-bottom: 4px;
}

.op-modal-stat-card .stat-value {
  font-size: 14px;
  font-weight: 700;
}

.op-modal-map-container {
  height: 280px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
}

.op-map-element {
  width: 100%;
  height: 100%;
}

.op-btn-secondary {
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  color: #475569 !important;
}

/* Colors */
.text-navy { color: #082C69 !important; }
.text-emerald { color: #059669 !important; }
.text-blue { color: #0284C7 !important; }
.text-amber { color: #D97706 !important; }
.gap-2 { gap: 8px; }
</style>
